import {
  anchorFromRects, type HighlightAnchor, type Rect,
} from '../core/page-geometry';

/**
 * The one impure step between a teacher's selection and an anchor.
 *
 * Everything this file does is *read the DOM*: which nodes the selection covers, what
 * rectangles the browser laid them out in, and where the page box sits on screen. The moment
 * those are numbers they go to `core/page-geometry`, which knows no DOM at all and is where
 * the arithmetic is tested. That split is the reason the round trip can be proved at a dozen
 * zoom levels without a browser, and the reason this file is nine lines of logic rather than
 * ninety.
 *
 * Two refusals worth naming:
 *
 * 1. **A selection outside the text layer is not a highlight.** A teacher selecting a comment,
 *    the material's title or the zoom readout is selecting a comment, a title or a readout. A
 *    highlight is a claim about the *document*, so the range has to lie inside the layer that
 *    is the document.
 * 2. **A collapsed selection is a click.** Browsers report a caret as a selection, and every
 *    stray tap on a page would otherwise leave a record — which matters more in pass 2, where
 *    the record is stored, than it does here, where it is not.
 */

/**
 * The page box on screen, as `getBoundingClientRect` reports it.
 *
 * Declared structurally rather than as `DOMRect` so a test can hand over a plain object.
 */
export interface ScreenBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface SelectionAnchorInput {
  /** `window.getSelection()`, passed in rather than reached for. */
  readonly selection: Selection | null;
  /** The text layer. A range that does not lie inside it is not about the document. */
  readonly layer: Node;
  /** The page box's position and size on screen, in CSS pixels. */
  readonly box: ScreenBox;
  /** Which page the selection is in. 1-based, read off the page box it landed in. */
  readonly page: number;
}

/**
 * A live selection, as an anchor — or `null` if it is not one.
 *
 * The rectangles come from the *range*, not from the spans: a selection that starts mid-word
 * and ends mid-word covers parts of two spans, and only the range knows that. They are
 * translated into the page box's own frame here, which is the last point at which anything
 * about the screen is involved.
 */
export function anchorFromSelection(input: SelectionAnchorInput): HighlightAnchor | null {
  const { selection, layer, box, page } = input;
  if (selection === null || selection.isCollapsed || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!layer.contains(range.commonAncestorContainer)) return null;

  const rects: Rect[] = Array.from(range.getClientRects(), (rect) => ({
    x: rect.left - box.left,
    y: rect.top - box.top,
    w: rect.width,
    h: rect.height,
  }));

  return anchorFromRects({
    page,
    quote: selection.toString(),
    rects,
    box: { width: box.width, height: box.height },
  });
}

/**
 * The page a selection landed on, in a document that is one long scroll.
 *
 * With a pager there was one page box on screen and the caller already knew its number. There
 * are forty now, so the *selection* has to say which one it is in — and it does, because a
 * range lies inside exactly one page's text layer. Reading it back off the DOM is one
 * `closest` call, which is cheaper and harder to get wrong than a registry of mounted pages
 * kept in step by hand.
 *
 * `stage` bounds the search: a range inside some other scrolling document on the same screen
 * is not a selection of this one.
 */
export function pageOfSelection(
  selection: Selection | null,
  stage: Element,
): { page: number; box: Element; layer: Element } | null {
  if (selection === null || selection.isCollapsed || selection.rangeCount === 0) return null;

  const node = selection.getRangeAt(0).commonAncestorContainer;
  const element = node.nodeType === 1 ? (node as Element) : node.parentElement;
  const box = element?.closest('.viewer-page') ?? null;
  if (box === null || !stage.contains(box)) return null;

  const page = Number(box.getAttribute('data-page'));
  if (!Number.isInteger(page) || page < 1) return null;

  const layer = box.querySelector('.viewer-text-layer');
  if (layer === null) return null;

  return { page, box, layer };
}

/**
 * Whatever is selected in the scrolling document, as an anchor — or `null`.
 *
 * The one call the viewer makes. It finds the page, measures its box, and hands both to the
 * pure arithmetic; nothing about which page is on screen, or where the stage is scrolled to,
 * is involved, because an anchor is a fact about the document rather than about the view of it.
 */
export function anchorFromDocumentSelection(input: {
  selection: Selection | null;
  stage: Element;
}): HighlightAnchor | null {
  const found = pageOfSelection(input.selection, input.stage);
  if (found === null) return null;

  return anchorFromSelection({
    selection: input.selection,
    layer: found.layer,
    box: found.box.getBoundingClientRect(),
    page: found.page,
  });
}

export type { HighlightAnchor };
